/** @jsx jsx */
import { SliderHandle, SliderInput, SliderTrack, SliderTrackHighlight } from '@reach/slider'
import '@reach/slider/styles.css'
import * as Sentry from '@sentry/browser'
import _ from 'lodash'
import { rgba } from 'polished'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { jsx, SxStyleProp } from 'theme-ui'
import { Box, Button, Flex, FormattedCurrency, Txt, Row, Col, Collapse } from '../../components'
import { AsyncActionButton } from '../../components/AsyncActionButton'
import { FormattedPercentage } from '../../components/FormattedPercentage'
import { PlayingCard } from '../../components/PlayingCard'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import theme from '../../gatsby-plugin-theme-ui'
import { globalDebugger } from '../../utils/globalDebugger'
import { GameMockType, IGameActions } from './types'
import { captureExceptionWithLabel } from '../../utils/sentry'
import { getFunction } from '../../utils/api'
import { IPositioningMeasurements } from './positionProps'
import { FormattedNumber } from '../../components/FormattedNumber'
import { format } from 'date-fns'
import { FormattedTime } from '../../components/FormattedTime'
import { UserProfilePicture } from '../../components/UserProfilePicture'
import { FormattedUserName } from '../../components/FormattedUserName'
import { PendingUserRequestButton } from './PendingUserRequest'
import { ButtonBlock } from '../../components/ButtonBlock'
import { CurrentUserOptionsModal } from './CurrentUserOptionsModal'

export const BottomOfPageDetails: React.FC<{
  controller: ClientGameHandController
  actions: IGameActions
  gameMockType?: GameMockType
  measurements: IPositioningMeasurements
}> = ({ controller, actions, gameMockType, measurements }) => {
  const { currentUserData, game, gameHand } = controller
  const [showCurrentUserModal, setShowCurrentUserModal] = useState(false)
  if (!measurements.isLandscapeMode) {
    return (
      <React.Fragment>
        <CurrentUserOptionsModal
          actions={actions}
          controller={controller}
          isOpen={showCurrentUserModal}
          onClose={() => setShowCurrentUserModal(false)}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            p: measurements.globalHorizontalPadding,
          }}
        >
          <Txt sx={{ color: 'lightText', fontSize: [0, null, null, null, null, 1], mb: 2 }}>
            Chips Left:{' '}
            <Txt
              sx={{
                color: 'darkBlue',
                fontWeight: 'bold',
                fontSize: [1, null, null, null, null, 2],
              }}
              as="span"
            >
              <FormattedCurrency amount={currentUserData.amountLeftToBet} />
            </Txt>
          </Txt>
          <Collapse
            isOpened={
              !!game.pendingUserEmailAccessRequests.length && currentUserData.role === 'ADMIN'
            }
          >
            <PendingUserRequestButton sx={{ fontSize: 1 }} variant="link" game={controller.game} />
          </Collapse>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            textAlign: 'right',
            p: measurements.globalHorizontalPadding,
          }}
        >
          <Flex sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <AsyncActionButton
              loadingVariant="light"
              disabled={
                currentUserData.tableState === 'OUT' ||
                (currentUserData.tableState === 'PLAYING' &&
                  _.values(gameHand.userTableStates).filter((val) => val === 'PLAYING').length <= 2)
              }
              errorLabel="setCurrentUserTableState"
              action={() =>
                actions.setCurrentUserTableState(
                  currentUserData.tableState === 'PLAYING' ? 'AWAY' : 'PLAYING',
                )
              }
              variant={currentUserData.tableState === 'PLAYING' ? 'lightGreen' : undefined}
              sx={{
                fontSize: 0,
                py: 2,
                px: 3,
                mr: 3,
                ...(currentUserData.tableState === 'PLAYING'
                  ? {}
                  : {
                      bg: 'background',
                      color: 'darkBlue',
                    }),
              }}
            >
              Status: {currentUserData.tableState}
            </AsyncActionButton>
            <Box>
              <ButtonBlock onClick={() => setShowCurrentUserModal(true)}>
                <UserProfilePicture
                  width={measurements.offTableUserInfoWidth / 1.5}
                  user={currentUserData}
                />
              </ButtonBlock>
            </Box>
          </Flex>
        </Box>
      </React.Fragment>
    )
  }
  return (
    <React.Fragment>
      <CurrentUserOptionsModal
        actions={actions}
        controller={controller}
        isOpen={showCurrentUserModal}
        onClose={() => setShowCurrentUserModal(false)}
      />

      <Flex
        css={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          minWidth: measurements.tableAndPlayersWidth,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            p: measurements.globalHorizontalPadding,
          }}
        >
          <Txt sx={{ color: 'lightText', fontSize: [0, null, null, null, null, 1] }}>
            Total Table Chips: <FormattedCurrency amount={controller.totalChips} />
          </Txt>
          <Txt sx={{ color: 'lightText', fontSize: [0, null, null, null, null, 1] }}>
            {!_.isNil(gameHand.rules.numberOfHandsUntilBlindIncrease) && (
              <Txt>
                Blinds are <FormattedCurrency amount={gameHand.rules.blindAmount / 2} />/
                <FormattedCurrency amount={gameHand.rules.blindAmount} /> - increase in{' '}
                <FormattedNumber amount={gameHand.rules.numberOfHandsUntilBlindIncrease} /> hands
              </Txt>
            )}
            <Txt>
              {gameHand.rules.gameType === 'TEXAS_HOLDEM_NO_LIMIT' ? 'NL' : 'Limit'} Holdem - Game
              started {format(new Date(game.datetimeCreated), 'LLL do')} at{' '}
              <FormattedTime format="K:mm aaa" datetime={new Date(game.datetimeCreated)} />
            </Txt>
          </Txt>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            textAlign: 'right',
            p: measurements.globalHorizontalPadding,
          }}
        >
          <Flex sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <Box sx={{ mr: 3 }}>
              <AsyncActionButton
                loadingVariant="light"
                disabled={
                  currentUserData.tableState === 'OUT' ||
                  (currentUserData.tableState === 'PLAYING' &&
                    _.values(gameHand.userTableStates).filter((val) => val === 'PLAYING').length <=
                      2)
                }
                errorLabel="setCurrentUserTableState"
                action={() =>
                  actions.setCurrentUserTableState(
                    currentUserData.tableState === 'PLAYING' ? 'AWAY' : 'PLAYING',
                  )
                }
                sx={{
                  fontSize: 0,
                  py: 2,
                  px: 3,
                  ...(currentUserData.tableState === 'PLAYING'
                    ? {
                        bg: 'lightGreen',
                        borderColor: 'lightGreen',
                        color: 'white',
                        textShadow: `1px 1px 6px rgba(0,0,0,.4)`,
                      }
                    : {
                        bg: 'background',
                        color: 'darkBlue',
                      }),
                }}
              >
                Status: {currentUserData.tableState}
              </AsyncActionButton>
              <Txt
                sx={{
                  color: 'darkBlue',
                  fontSize: 1,
                }}
              >
                <FormattedUserName user={currentUserData} />
              </Txt>
            </Box>
            <Box>
              <ButtonBlock onClick={() => setShowCurrentUserModal(true)}>
                <UserProfilePicture
                  width={measurements.offTableUserInfoWidth / 2}
                  user={currentUserData}
                />
              </ButtonBlock>
            </Box>
          </Flex>
          <Txt sx={{ color: 'lightText' }}>
            Chips Left:{' '}
            <Txt
              sx={{
                color: 'darkBlue',
                fontWeight: 'bold',
                fontSize: [0, null, null, null, null, 2],
              }}
              as="span"
            >
              <FormattedCurrency amount={currentUserData.amountLeftToBet} />{' '}
              <Txt as="span" sx={{ fontSize: [0, null, null, null, null, 1] }}>
                ({Math.floor((currentUserData.amountLeftToBet / controller.totalChips) * 100)}% of
                table)
              </Txt>
            </Txt>
          </Txt>
          <Collapse
            isOpened={
              !!game.pendingUserEmailAccessRequests.length && currentUserData.role === 'ADMIN'
            }
          >
            <PendingUserRequestButton variant="link" game={controller.game} />
          </Collapse>
          {gameMockType === 'manually-trigger-next-state' && (
            <Txt>
              <Button onClick={() => actions.check()} variant="link">
                Next State
              </Button>
            </Txt>
          )}
        </Box>
      </Flex>
    </React.Fragment>
  )
}
